import { useState, useEffect } from 'react'
import { Building2, Globe, Tag, FileText, TrendingUp, ArrowRightCircle, Loader2, AlertCircle, MapPin, Edit2 } from 'lucide-react'
import PlanCombobox from './PlanCombobox'
import PlanSelectionModal from './PlanSelectionModal'
import BusinessScopeStep from './onboarding/BusinessScopeStep'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import type { BusinessScope } from '../types/brand'
import Modal from './Modal'

interface Brand {
  id: string
  userBrandId?: string
  companyName: string
  domain?: string
  website?: string
  industry?: string
  description?: string
  aiIndexScore?: number
  subscriptionProduct?: 'STARTER' | 'GROWTH' | 'PRO' | null
  subscriptionStatus?: 'ACTIVE' | 'TRIAL' | 'PAST_DUE' | 'CANCELED' | 'UNPAID'
  currentPeriodEnd?: string | null
  cancelAtPeriodEnd?: boolean
  trialEndsAt?: string | null
  analysisFrequency?: 'MONTHLY' | 'WEEKLY'
  businessScope?: string
  serviceAreas?: string[]
  primaryLocation?: string
}

interface BrandDetailModalProps {
  brand: Brand
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  onActivateBrand: (brandId: string) => Promise<void>
  isActiveBrand: boolean
}

export default function BrandDetailModal({
  brand,
  isOpen,
  onClose,
  onSuccess,
  onActivateBrand,
  isActiveBrand
}: BrandDetailModalProps) {
  const [activating, setActivating] = useState(false)
  const [planSelectionOpen, setPlanSelectionOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<{ id: 'STARTER' | 'GROWTH' | 'PRO'; name: string; monthlyPrice: number } | null>(null)

  // Business scope editing state
  const [editingBusinessScope, setEditingBusinessScope] = useState(false)
  const [savingBusinessScope, setSavingBusinessScope] = useState(false)
  const [businessScopeData, setBusinessScopeData] = useState({
    businessScope: brand.businessScope || 'NATIONAL',
    serviceAreas: brand.serviceAreas || [],
    primaryLocation: brand.primaryLocation || undefined
  })

  // Fetch latest brand data when modal opens
  useEffect(() => {
    if (isOpen && brand.id) {
      const fetchBrandData = async () => {
        try {
          const response = await api.get(`/brands/${brand.id}`)
          const brandData = response.data
          setBusinessScopeData({
            businessScope: brandData.businessScope || 'NATIONAL',
            serviceAreas: brandData.serviceAreas || [],
            primaryLocation: brandData.primaryLocation || undefined
          })
        } catch (error) {
          console.error('[BrandDetailModal] Failed to fetch brand data:', error)
        }
      }
      fetchBrandData()
    }
  }, [isOpen, brand.id])

  const handleActivate = async () => {
    setActivating(true)
    try {
      await onActivateBrand(brand.id)
    } finally {
      setActivating(false)
    }
  }

  const handlePlanSelect = (plan: { id: 'NOT_ACTIVATED' | 'TRIAL' | 'STARTER' | 'GROWTH' | 'PRO'; name: string; monthlyPrice: number }) => {
    // Don't allow selecting TRIAL or NOT_ACTIVATED - they're just for display
    if (plan.id === 'TRIAL' || plan.id === 'NOT_ACTIVATED') return

    // If selecting a different plan, open the modal
    if (plan.id !== brand.subscriptionProduct) {
      setSelectedPlan(plan as { id: 'STARTER' | 'GROWTH' | 'PRO'; name: string; monthlyPrice: number })
      setPlanSelectionOpen(true)
    }
  }

  const getCurrentPlan = (): 'TRIAL' | 'STARTER' | 'GROWTH' | 'PRO' | null => {
    if (brand.subscriptionStatus === 'TRIAL') return 'TRIAL'
    return brand.subscriptionProduct || null
  }

  const isNewCustomer = !brand.subscriptionProduct || brand.subscriptionStatus === 'TRIAL'

  // Check if subscription is expired/inactive
  const isTrialExpired = () => {
    if (!brand.trialEndsAt) return false
    const now = new Date()
    const trialEnd = new Date(brand.trialEndsAt)
    return trialEnd.getTime() < now.getTime()
  }

  const hasExpiredSubscription = () => {
    if (!brand.subscriptionProduct && brand.subscriptionStatus !== 'TRIAL') return true
    if (brand.subscriptionStatus === 'CANCELED' || brand.subscriptionStatus === 'UNPAID') return true
    if (brand.subscriptionStatus === 'TRIAL' && isTrialExpired()) return true
    return false
  }

  const getExpirationMessage = () => {
    if (brand.subscriptionStatus === 'TRIAL' && isTrialExpired()) {
      return 'Your 7-day trial has ended. Select a plan below to continue using this brand.'
    }
    if (brand.subscriptionStatus === 'CANCELED') {
      return 'This brand\'s subscription has been canceled. Select a plan below to reactivate.'
    }
    if (brand.subscriptionStatus === 'UNPAID') {
      return 'Payment failed for this brand. Please update your payment method and select a plan.'
    }
    return 'This brand doesn\'t have an active subscription. Select a plan below to activate.'
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <Modal.Header onClose={onClose}>
        <Building2 className="h-6 w-6 text-indigo-600" />
        <h2 className="text-xl font-semibold text-gray-900">{brand.companyName}</h2>
        {isActiveBrand && (
          <span className="px-2 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-700 rounded">
            Active
          </span>
        )}
      </Modal.Header>
      <Modal.Body className="p-0">
        {/* Expired Subscription Warning */}
        {isActiveBrand && hasExpiredSubscription() && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900 mb-1">Subscription Required</h3>
                <p className="text-sm text-red-700">{getExpirationMessage()}</p>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* AI Index Score */}
          {brand.aiIndexScore !== undefined && (
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">AI Visibility Score</p>
                  <p className="text-4xl font-bold text-indigo-600">{brand.aiIndexScore.toFixed(1)}</p>
                </div>
                <TrendingUp className="h-12 w-12 text-indigo-400" />
              </div>
            </div>
          )}

          {/* Brand Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Brand Information</h3>

            {brand.domain && (
              <div className="flex items-start space-x-3">
                <Globe className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Website</p>
                  <a
                    href={`https://${brand.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:underline"
                  >
                    {brand.domain}
                  </a>
                </div>
              </div>
            )}

            {brand.industry && (
              <div className="flex items-start space-x-3">
                <Tag className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Industry</p>
                  <p className="text-sm text-gray-900">{brand.industry}</p>
                </div>
              </div>
            )}

            {brand.description && (
              <div className="flex items-start space-x-3">
                <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p className="text-sm text-gray-700">{brand.description}</p>
                </div>
              </div>
            )}

            {/* Business Scope */}
            <div className="flex items-start space-x-3">
              <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-gray-500">Business Scope</p>
                  <button
                    onClick={() => setEditingBusinessScope(!editingBusinessScope)}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                  >
                    <Edit2 className="h-3 w-3" />
                    {editingBusinessScope ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                {!editingBusinessScope ? (
                  <div className="text-sm text-gray-900">
                    <p className="font-medium capitalize">
                      {businessScopeData.businessScope.toLowerCase().replace('_', ' ')}
                    </p>
                    {businessScopeData.serviceAreas && businessScopeData.serviceAreas.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {businessScopeData.serviceAreas.map((area, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-gray-600">
                            <MapPin className="h-3 w-3" />
                            <span>{area}</span>
                            {area === businessScopeData.primaryLocation && (
                              <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] font-medium">
                                Primary
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Business Scope Editor */}
          {editingBusinessScope && (
            <div className="border-t border-gray-200 pt-6">
              <BusinessScopeStep
                initialScope={businessScopeData.businessScope as BusinessScope}
                initialServiceAreas={businessScopeData.serviceAreas}
                initialPrimaryLocation={businessScopeData.primaryLocation}
                onSave={async (data) => {
                  try {
                    setSavingBusinessScope(true)
                    await api.patch(`/brands/${brand.id}`, {
                      businessScope: data.businessScope,
                      serviceAreas: data.serviceAreas,
                      primaryLocation: data.primaryLocation
                    })

                    setBusinessScopeData({
                      businessScope: data.businessScope,
                      serviceAreas: data.serviceAreas,
                      primaryLocation: data.primaryLocation || undefined
                    })
                    setEditingBusinessScope(false)
                    toast.success('Business scope updated successfully')
                    onSuccess() // Refresh parent component
                  } catch (error: any) {
                    console.error('[BrandDetailModal] Failed to update business scope:', error)
                    toast.error(error.response?.data?.message || 'Failed to update business scope')
                  } finally {
                    setSavingBusinessScope(false)
                  }
                }}
                isProcessing={savingBusinessScope}
              />
            </div>
          )}

          {/* Subscription Management */}
          {brand.userBrandId && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Subscription Plan</h3>
              <PlanCombobox
                currentPlan={getCurrentPlan()}
                onPlanSelect={handlePlanSelect}
              />

              {/* Trial/Billing Info */}
              {brand.subscriptionStatus === 'TRIAL' && brand.trialEndsAt && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>Trial ends:</strong> {new Date(brand.trialEndsAt).toLocaleDateString()}
                  </p>
                </div>
              )}
              {brand.currentPeriodEnd && brand.subscriptionStatus === 'ACTIVE' && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Next billing:</strong> {new Date(brand.currentPeriodEnd).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {!isActiveBrand && (
            <div className="border-t border-gray-200 pt-6">
              {!brand.subscriptionProduct && brand.subscriptionStatus !== 'TRIAL' ? (
                <div className="space-y-3">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>No active subscription</strong> - Select a plan above to activate this brand
                    </p>
                  </div>
                  <button
                    disabled={true}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
                  >
                    <ArrowRightCircle className="h-5 w-5" />
                    <span>Switch to This Brand</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleActivate}
                  disabled={activating}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {activating ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Switching...</span>
                    </>
                  ) : (
                    <>
                      <ArrowRightCircle className="h-5 w-5" />
                      <span>Switch to This Brand</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </Modal.Body>

      {/* Plan Selection Modal - already uses Portal via Modal component */}
      {selectedPlan && brand.userBrandId && (
        <PlanSelectionModal
          isOpen={planSelectionOpen}
          onClose={() => {
            setPlanSelectionOpen(false)
            setSelectedPlan(null)
          }}
          plan={selectedPlan}
          userBrandId={brand.userBrandId}
          isNewCustomer={isNewCustomer}
          onSuccess={() => {
            setPlanSelectionOpen(false)
            setSelectedPlan(null)
            onSuccess()
          }}
        />
      )}
    </Modal>
  )
}
